import { Controller } from "@hotwired/stimulus"

// Track all dropdown instances for closing others
const openDropdowns = new Set()

export default class extends Controller {
  static targets = ["menu"]

  connect() {
    // Ensure menu starts hidden
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
    }
    // Add click event listener to window
    this.boundHideHandler = this.hide.bind(this)
    this.boundScrollHandler = this.updatePosition.bind(this)
    this.boundResizeHandler = this.updatePosition.bind(this)
    window.addEventListener("click", this.boundHideHandler)
  }

  disconnect() {
    // Clean up event listeners
    window.removeEventListener("click", this.boundHideHandler)
    window.removeEventListener("scroll", this.boundScrollHandler, true)
    window.removeEventListener("resize", this.boundResizeHandler)
    openDropdowns.delete(this)
  }

  toggle(event) {
    event.stopPropagation()
    const isHidden = this.menuTarget.classList.contains("hidden")

    if (isHidden) {
      this.show()
    } else {
      this.closeMenu()
    }
  }

  show() {
    // Close all other open dropdowns first
    openDropdowns.forEach(dropdown => {
      if (dropdown !== this) {
        dropdown.closeMenu()
      }
    })

    // Apply fixed positioning to escape overflow constraints
    this.applyFixedPositioning()
    this.menuTarget.classList.remove("hidden")
    openDropdowns.add(this)

    // Add scroll and resize listeners while open
    window.addEventListener("scroll", this.boundScrollHandler, true)
    window.addEventListener("resize", this.boundResizeHandler)
  }

  closeMenu() {
    this.menuTarget.classList.add("hidden")
    this.removeFixedPositioning()
    openDropdowns.delete(this)
    window.removeEventListener("scroll", this.boundScrollHandler, true)
    window.removeEventListener("resize", this.boundResizeHandler)
  }

  hide(event) {
    if (this.element.contains(event.target)) {
      return // Don't hide if click is inside the dropdown
    }
    this.closeMenu()
  }

  applyFixedPositioning() {
    const button = this.element.querySelector("button")
    if (!button) return

    const rect = button.getBoundingClientRect()
    const menuHeight = this.menuTarget.offsetHeight || 200 // Estimate if not yet rendered
    const viewportHeight = window.innerHeight

    // Position fixed relative to viewport
    this.menuTarget.style.position = "fixed"
    this.menuTarget.style.right = `${window.innerWidth - rect.right}px`

    // Check if dropdown would go below viewport
    if (rect.bottom + menuHeight > viewportHeight) {
      // Open upward
      this.menuTarget.style.bottom = `${viewportHeight - rect.top}px`
      this.menuTarget.style.top = "auto"
    } else {
      // Open downward (default)
      this.menuTarget.style.top = `${rect.bottom}px`
      this.menuTarget.style.bottom = "auto"
    }

    this.menuTarget.style.left = "auto"
    this.menuTarget.style.zIndex = "9999"
  }

  removeFixedPositioning() {
    this.menuTarget.style.position = ""
    this.menuTarget.style.top = ""
    this.menuTarget.style.right = ""
    this.menuTarget.style.bottom = ""
    this.menuTarget.style.left = ""
    this.menuTarget.style.zIndex = ""
  }

  updatePosition() {
    if (!this.menuTarget.classList.contains("hidden")) {
      this.applyFixedPositioning()
    }
  }
}
